import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Heart, Eye, MessageSquare, Calendar, Award, Bell, ChevronRight } from 'lucide-react'

export const Route = createFileRoute('/feature/parent')({
  component: ParentFeaturesPage,
})

interface Feature {
  id: number
  icon: JSX.Element
  title: string
  summary: string
  keyPoints: string[]
}

function ParentFeaturesPage() {
  const { t } = useTranslation('features')
  const mainColor = '#525FE1'

  const features: Feature[] = [
    {
      id: 1,
      icon: <Eye className="w-8 h-8" />,
      title: t('parent.monitoring.title'),
      summary: t('parent.monitoring.description'),
      keyPoints: ['Real-time progress', 'Grade tracking', 'Performance analytics']
    },
    {
      id: 2,
      icon: <MessageSquare className="w-8 h-8" />,
      title: t('parent.communication.title'),
      summary: t('parent.communication.description'),
      keyPoints: ['Direct messaging', 'School announcements', 'Virtual conferences']
    },
    {
      id: 3,
      icon: <Calendar className="w-8 h-8" />,
      title: t('parent.schedule.title'),
      summary: t('parent.schedule.description'),
      keyPoints: ['Class schedule view', 'Assignment tracking', 'Event notifications']
    },
    {
      id: 4,
      icon: <Award className="w-8 h-8" />,
      title: t('parent.progress.title'),
      summary: t('parent.progress.description'),
      keyPoints: ['Detailed reports', 'Performance insights', 'Improvement areas']
    },
    {
      id: 5,
      icon: <Bell className="w-8 h-8" />,
      title: t('parent.notifications.title'),
      summary: t('parent.notifications.description'),
      keyPoints: ['Timely alerts', 'Grade notifications', 'Event reminders']
    },
    {
      id: 6,
      icon: <Heart className="w-8 h-8" />,
      title: t('parent.support.title'),
      summary: t('parent.support.description'),
      keyPoints: ['Study guides', 'Homework help', 'Educational resources']
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12 md:py-16">
        <div className="mb-12">
          <a
            href="/features"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToFeatures')}
          </a>
          
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-sm" style={{ backgroundColor: mainColor }}>
              <Heart className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('parent.title')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('parent.subtitle')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {features.map((feature) => (
            <div 
              key={feature.id} 
              className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-200 overflow-hidden flex flex-col"
            >
              <div 
                className="px-6 py-5"
                style={{ backgroundColor: mainColor }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
                    <div className="text-white">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white leading-tight">
                    {feature.title}
                  </h3>
                </div>
              </div>
              
              <div className="px-6 py-5 flex flex-col flex-grow">
                <p className="text-gray-700 text-sm leading-relaxed mb-5">
                  {feature.summary}
                </p>
                
                <div className="mb-5 flex-grow">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Key Points
                  </h4>
                  <ul className="space-y-2.5">
                    {feature.keyPoints.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start text-sm text-gray-600">
                        <span 
                          className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0"
                          style={{ backgroundColor: mainColor }}
                        ></span>
                        <span className="leading-relaxed">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div
                  className="mt-auto flex items-center justify-between px-4 py-3 rounded-lg border transition-all duration-200 hover:shadow-sm text-sm font-medium cursor-not-allowed opacity-60"
                  style={{ 
                    backgroundColor: '#f8f9ff', 
                    borderColor: `${mainColor}30`,
                    color: mainColor
                  }}
                >
                  <span>Learn More</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-8 md:p-10 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t('parent.benefits.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('parent.benefits.awareness.title')}
              </h3>
              <ul className="space-y-2.5 text-gray-600">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('parent.benefits.awareness.item1')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('parent.benefits.awareness.item2')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('parent.benefits.awareness.item3')}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('parent.benefits.involvement.title')}
              </h3>
              <ul className="space-y-2.5 text-gray-600">
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('parent.benefits.involvement.item1')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('parent.benefits.involvement.item2')}
                </li>
                <li className="flex items-start">
                  <span className="w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: mainColor }}></span>
                  {t('parent.benefits.involvement.item3')}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center max-w-2xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {t('parent.cta.title')}
          </h3>
          <p className="text-gray-600 mb-8">
            {t('parent.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/register"
              className="text-white px-6 py-3 rounded-lg font-medium transition-colors hover:opacity-90"
              style={{ backgroundColor: mainColor }}
            >
              {t('parent.cta.getStarted')}
            </a>
            <a
              href="/demo"
              className="border px-6 py-3 rounded-lg font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: mainColor, color: mainColor }}
            >
              {t('parent.cta.scheduleDemo')}
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
